import React, { useState } from "react";
import Button from "src/components/form/Button";
import Input from "src/components/form/Input";
import { apiUrl } from "src/config/host";
import { encodeRelayState } from "src/helpers/urlBuilder";
import { MultiSsoModal } from "./MultiSsoModal";
import TwoFactorView from "./TwoFactorView";
import { TotpRecoveryForm } from "./TotpRecoveryForm";
import { useSiteSettings } from "src/context/SiteSettingsContext";

export const DefaultForm = ({
  ssos,
  otp,
  disabled,
  setOtp,
  resendOtp,
  requireTwoFactor,
  disabledResendOtp,
  resendOtpSecond,
  email,
  setEmail,
  onSubmit,
  password,
  setPassword,
  verifyOtp,
  lockout,
  otpSecond,
  loadingTrackLogin,
  setCurrentStep,
  twoFactorDetails,
  isRecoveryMode,
  setIsRecoveryMode,
  handleTotpRecovery,
}) => {
  const { site } = useSiteSettings();
  const [ssoModal, setSsoModal] = useState(false);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  return (
    <div>
      {ssos?.length > 1 && site.dual_signin_for_user === false ? (
        <MultiSsoModal
          isModal={false}
          ssos={ssos}
          site={site}
          ssoModal={ssoModal}
          setSsoModal={setSsoModal}
        />
      ) : (
        <div>
          <div className="flex w-full flex-col ">
            {requireTwoFactor ? (
              isRecoveryMode ? (
                <TotpRecoveryForm
                  site={site}
                  disabled={disabled}
                  onCancel={() => setIsRecoveryMode(false)}
                  onRecoverySubmit={handleTotpRecovery}
                />
              ) : (
                <TwoFactorView
                  twoFactorDetails={twoFactorDetails}
                  email={email}
                  otp={otp}
                  setOtp={setOtp}
                  verifyOtp={verifyOtp}
                  resendOtp={resendOtp}
                  resendOtpSecond={resendOtpSecond}
                  disabledResendOtp={disabledResendOtp}
                  formatTime={formatTime}
                  site={site}
                  lockout={lockout}
                  otpSecond={otpSecond}
                  disabled={disabled}
                  setIsRecoveryMode={setIsRecoveryMode}
                />
              )
            ) : (
              <form className="flex w-full flex-col gap-y-2">
                <div className="w-full">
                  <Input
                    inline
                    labelClasses={"font-medium mb-0"}
                    inputValue={email}
                    onChange={(e) => setEmail(e.target.value)}
                    label={"Email"}
                    autoComplete="username"
                    inputType={"email"}
                    inputName={"email"}
                    inputClasses={"font-medium"}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        onSubmit(e);
                      }
                    }}
                  />
                </div>
                <div className="w-full">
                  <Input
                    inline={true}
                    labelClasses={"font-medium mb-0"}
                    inputValue={password}
                    onChange={(e) => setPassword(e.target.value)}
                    label={"Password"}
                    autoComplete="current-password"
                    inputType={"password"}
                    inputName={"password"}
                    inputClasses={"font-medium"}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        onSubmit(e);
                      }
                    }}
                  />
                </div>
              </form>
            )}
          </div>
          {!requireTwoFactor && (
            <>
              <div className="flex justify-end">
                <Button
                  buttonClasses={"!px-0 !h-auto !bg-transparent !rounded-none !text-sm !my-2"}
                  buttonHasLink={true}
                  buttonLink={"/forgot-password"}
                  buttonLabel={"Forgot Password?"}
                  buttonLabelClasses={"!text-sm hover:!underline !text-slate-800 !font-medium"}
                />
              </div>
              <div className="mt-2 grid gap-y-4">
                <Button
                  isDisable={disabled || lockout || otpSecond > 0 || loadingTrackLogin}
                  buttonClasses={"w-full"}
                  loading={disabled}
                  buttonType={"Submit"}
                  buttonLabelClasses={" !font-normal "}
                  buttonLabel={!lockout ? (otpSecond > 0 ? `You've been blocked for ${formatTime(otpSecond)}` : requireTwoFactor ? "Submit" : "Login") : `Your login has been blocked for ${lockout === 60 ? "01:00" : `00:${lockout < 10 ? "0" : ""}${lockout}`}`}
                  buttonFunction={(e) => {
                    e.preventDefault();
                    !requireTwoFactor ? onSubmit(e) : verifyOtp(e);
                  }}
                  buttonStyles={{
                    borderColor: site?.highlight_color,
                    backgroundColor: site?.highlight_color,
                  }}
                />
              </div>
              {site?.workspace_login_enabled && (
                <div>
                  <div className="my-5 flex items-center justify-between">
                    <div className="h-[1px] w-1/3 bg-gray-300"></div>
                    <span className="text-xs font-medium text-gray-500 sm:text-sm">OR</span>
                    <div className="h-[1px] w-1/3 bg-gray-300"></div>
                  </div>
                  <Button
                    buttonClasses={"w-full"}
                    isDisable={lockout}
                    buttonLabelClasses={" !font-normal "}
                    buttonLabel={"Login with SSO"}
                    buttonFunction={() => setCurrentStep("workspace_login")}
                    buttonStyles={{
                      borderColor: site?.highlight_color,
                      backgroundColor: site?.highlight_color,
                    }}
                  />
                </div>
              )}
              {ssos.length > 0 && !site?.workspace_login_enabled && (
                <div className="flex min-h-[100px] items-center justify-center">
                  <div className="w-full">
                    <div className="my-5 flex items-center justify-between">
                      <div className="h-[1px] w-1/4 bg-gray-300"></div>
                      <span className="text-xs font-medium text-gray-500 sm:text-sm">{ssos?.length === 1 ? "Or continue with SSO" : "OR"}</span>
                      <div className="h-[1px] w-1/4 bg-gray-300"></div>
                    </div>
                    <div className="mt-4">
                      <Button
                        buttonHasLink={false}
                        buttonClasses={"w-full"}
                        buttonType={"button"}
                        buttonLabel={site.sso_button_text ? site.sso_button_text : ssos[0]?.provider_name}
                        buttonLabelClasses={"!font-medium"}
                        buttonStyles={{
                          borderColor: site?.sso_button_text_color,
                          color: site?.sso_button_text_color || "#fff",
                          backgroundColor: site?.sso_button_color,
                        }}
                        buttonFunction={() => {
                          if (ssos?.length === 1 && !site.multiple_sso_login_enabled) {
                            window.location.href = `${apiUrl}/login-sso?RelayState=${encodeRelayState({
                              origin: window.location.origin,
                              sso_id: ssos[0]?._id,
                            })}`;
                          } else {
                            setSsoModal(true);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      <MultiSsoModal
        ssos={ssos}
        site={site}
        ssoModal={ssoModal}
        setSsoModal={setSsoModal}
      />
    </div>
  );
};
